import type BaseWidget from "widgets/BaseWidget";

/**
 * EE-seam widget loaders (Decision 0.4-6 option (c)): Wave-1+ sources live on
 * the CE-shared `widgets/wds/` path, registration happens EE-side. See
 * constants.ts for the map and the rationale.
 */
export const EEWDSWidgets: [string, () => Promise<typeof BaseWidget>][] = [
  [
    "WDS_AI_CHAT_WIDGET",
    async () =>
      import("ee/modules/ui-builder/ui/wds/WDSAIChatWidget").then(
        (m) => m.WDSAIChatWidget,
      ),
  ],
  [
    "WDS_ANVIL_TABLE_WIDGET",
    async () =>
      import("ee/modules/ui-builder/ui/wds/WDSAnvilTableWidget").then(
        (m) => m.WDSAnvilTableWidget,
      ),
  ],
  // Wave 1 (2026-07-05)
  [
    "WDS_IMAGE_WIDGET",
    async () =>
      import("widgets/wds/WDSImageWidget").then((m) => m.WDSImageWidget),
  ],
  [
    "WDS_DIVIDER_WIDGET",
    async () =>
      import("widgets/wds/WDSDividerWidget").then((m) => m.WDSDividerWidget),
  ],
  [
    "WDS_RATE_WIDGET",
    async () =>
      import("widgets/wds/WDSRateWidget").then((m) => m.WDSRateWidget),
  ],
  [
    "WDS_BADGE_WIDGET",
    async () =>
      import("widgets/wds/WDSBadgeWidget").then((m) => m.WDSBadgeWidget),
  ],
  [
    "WDS_AVATAR_WIDGET",
    async () =>
      import("widgets/wds/WDSAvatarWidget").then((m) => m.WDSAvatarWidget),
  ],
  [
    "WDS_LINK_WIDGET",
    async () =>
      import("widgets/wds/WDSLinkWidget").then((m) => m.WDSLinkWidget),
  ],
  [
    "WDS_CODE_WIDGET",
    async () =>
      import("widgets/wds/WDSCodeWidget").then((m) => m.WDSCodeWidget),
  ],
  // Wave 1b (2026-07-05)
  [
    "WDS_PROGRESS_WIDGET",
    async () =>
      import("widgets/wds/WDSProgressWidget").then((m) => m.WDSProgressWidget),
  ],
  [
    "WDS_SLIDER_WIDGET",
    async () =>
      import("widgets/wds/WDSSliderWidget").then((m) => m.WDSSliderWidget),
  ],
  [
    "WDS_FILE_UPLOAD_WIDGET",
    async () =>
      import("widgets/wds/WDSFileUploadWidget").then(
        (m) => m.WDSFileUploadWidget,
      ),
  ],
  [
    "WDS_SEARCH_WIDGET",
    async () =>
      import("widgets/wds/WDSSearchWidget").then((m) => m.WDSSearchWidget),
  ],
  [
    "WDS_CHIP_GROUP_WIDGET",
    async () =>
      import("widgets/wds/WDSChipGroupWidget").then(
        (m) => m.WDSChipGroupWidget,
      ),
  ],
  // Wave 2 (2026-07-06) — canvas hosts.
  [
    "WDS_TABS_WIDGET",
    async () =>
      import("widgets/wds/WDSTabsWidget").then((m) => m.WDSTabsWidget),
  ],
  [
    "WDS_CARD_WIDGET",
    async () =>
      import("widgets/wds/WDSCardWidget").then((m) => m.WDSCardWidget),
  ],
  // Wave 3 (2026-07-06)
  [
    "WDS_CHAT_WIDGET",
    async () =>
      import("ee/modules/ui-builder/ui/wds/WDSChatWidget").then(
        (m) => m.WDSChatWidget,
      ),
  ],
  [
    "WDS_EMBED_WIDGET",
    async () =>
      import("widgets/wds/WDSEmbedWidget").then((m) => m.WDSEmbedWidget),
  ],
  [
    "WDS_MEDIA_WIDGET",
    async () =>
      import("widgets/wds/WDSMediaWidget").then((m) => m.WDSMediaWidget),
  ],
  [
    "WDS_MAP_CHART_WIDGET",
    async () =>
      import("widgets/wds/WDSMapChartWidget").then((m) => m.WDSMapChartWidget),
  ],
  [
    "WDS_TREE_SELECT_WIDGET",
    async () =>
      import("widgets/wds/WDSTreeSelectWidget").then(
        (m) => m.WDSTreeSelectWidget,
      ),
  ],
  [
    "WDS_SHEET_WIDGET",
    async () =>
      import("widgets/wds/WDSSheetWidget").then((m) => m.WDSSheetWidget),
  ],
  [
    "WDS_CAROUSEL_WIDGET",
    async () =>
      import("widgets/wds/WDSCarouselWidget").then((m) => m.WDSCarouselWidget),
  ],
  [
    "WDS_FAB_WIDGET",
    async () => import("widgets/wds/WDSFabWidget").then((m) => m.WDSFabWidget),
  ],
  [
    "WDS_LIST_VIEW_WIDGET",
    async () =>
      import("widgets/wds/WDSListViewWidget").then((m) => m.WDSListViewWidget),
  ],
  // M4 Tier 1 (2026-08-18)
  [
    "WDS_DATE_RANGE_PICKER_WIDGET",
    async () =>
      import("widgets/wds/WDSDateRangePickerWidget").then(
        (m) => m.WDSDateRangePickerWidget,
      ),
  ],
  [
    "WDS_COLOR_PICKER_WIDGET",
    async () =>
      import("widgets/wds/WDSColorPickerWidget").then(
        (m) => m.WDSColorPickerWidget,
      ),
  ],
  // M4 Tier 2 (2026-08-19)
  [
    "WDS_CODE_SCANNER_WIDGET",
    async () =>
      import("widgets/wds/WDSCodeScannerWidget").then(
        (m) => m.WDSCodeScannerWidget,
      ),
  ],
  [
    "WDS_TREE_WIDGET",
    async () =>
      import("widgets/wds/WDSTreeWidget").then((m) => m.WDSTreeWidget),
  ],
  [
    "WDS_SIGNATURE_WIDGET",
    async () =>
      import("widgets/wds/WDSSignatureWidget").then(
        (m) => m.WDSSignatureWidget,
      ),
  ],
  // M4 Tier 3 (2026-08-20). This loader pulls the WIDGET module only —
  // TinyMCE sits behind a React.lazy boundary inside getWidgetView(), because
  // loadAllWidgets() resolves every entry here at editor bootstrap and again
  // in the eval worker.
  [
    "WDS_RICH_TEXT_EDITOR_WIDGET",
    async () =>
      import("widgets/wds/WDSRichTextEditorWidget").then(
        (m) => m.WDSRichTextEditorWidget,
      ),
  ],
];
